import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MIN_SIZE = 0, MAX_SIZE = 12;
const MODES = [
  { key: 'build', label: 'Build a Heap' },
  { key: 'extract', label: 'Extract the Root' },
  { key: 'heapify', label: 'Heapify an Array' },
  { key: 'kth', label: 'Kth Largest Tracker' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Heap index math ────────────────────────────────────────────── */
const parentOf = i => Math.floor((i - 1) / 2);
const leftOf = i => 2 * i + 1;
const rightOf = i => 2 * i + 2;
const better = (a, b, isMin) => (isMin ? a < b : a > b);   // does a belong closer to the root than b?

/* ─── Array row with parent/child tag support ────────────────────── */
const HeapRow = ({ array, getBoxState }) => (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 26, paddingBottom: 20 }}>
    {array.map((val, i) => {
      const st = getBoxState ? getBoxState(i) : {};
      return (
        <div key={i} style={{ position: 'relative' }}>
          {st.tag && <span style={{ position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)', fontSize: 9.5, fontWeight: 800, color: st.tagColor || V1, whiteSpace: 'nowrap' }}>{st.tag}</span>}
          <div style={{
            width: 46, height: 46, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 800, fontFamily: "'Inter',sans-serif",
            background: st.bg || 'var(--bg3)', color: st.color || 'var(--text)',
            border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
          }}>{val}</div>
          <span style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', bottom: -18, fontSize: 10, fontWeight: 700, color: 'var(--muted)' }}>{i}</span>
        </div>
      );
    })}
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);
const MinMaxToggle = ({ isMin, setIsMin }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    <button onClick={() => setIsMin(true)} style={{ ...smallBtn(isMin ? V1 : 'var(--bg3)'), color: isMin ? '#fff' : 'var(--muted)' }}>Min-Heap</button>
    <button onClick={() => setIsMin(false)} style={{ ...smallBtn(!isMin ? V1 : 'var(--bg3)'), color: !isMin ? '#fff' : 'var(--muted)' }}>Max-Heap</button>
  </div>
);

/* ─── Build a Heap (insert + sift-up) ────────────────────────────── */
const BuildMode = () => {
  const [isMin, setIsMin] = useState(true);
  const [heap, setHeap] = useState([]);
  const [value, setValue] = useState('');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const insert = () => {
    if (value.trim() === '' || isNaN(Number(value)) || heap.length >= MAX_SIZE) return;
    const v = Number(value);
    const arr = [...heap, v];
    let i = arr.length - 1;
    const built = [{ snapshot: [...arr], i, note: `Append ${v} at index ${i} (the next free slot).` }];
    while (i > 0) {
      const p = parentOf(i);
      if (better(arr[i], arr[p], isMin)) {
        [arr[i], arr[p]] = [arr[p], arr[i]];
        built.push({ snapshot: [...arr], i: p, swapped: [i, p], note: `${arr[p]} ${isMin ? '<' : '>'} parent — swap with index ${p}.` });
        i = p;
      } else {
        built.push({ snapshot: [...arr], i, note: `${arr[i]} is in the right place relative to its parent — sift-up stops.`, done: true });
        break;
      }
    }
    if (i === 0 && built[built.length - 1].i !== 0) built.push({ snapshot: [...arr], i: 0, note: 'Reached the root — sift-up stops.', done: true });
    setSteps(built); setStep(0);
    setValue('');
  };
  const apply = () => {
    if (!steps) return;
    setHeap(steps[steps.length - 1].snapshot);
    setSteps(null); setStep(0);
  };
  const reset = () => { setHeap([]); setSteps(null); setStep(0); };

  const cur = steps ? steps[step] : null;
  const displayArr = cur ? cur.snapshot : heap;
  const getBoxState = i => {
    if (!cur) return {};
    if (cur.swapped && cur.swapped.includes(i)) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff' };
    if (i === cur.i) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tag: 'moving up', tagColor: V2 };
    return {};
  };

  const code = {
    lines: [
      'void insert(int val) {',
      '    heap.add(val);',
      '    int i = heap.size() - 1;',
      `    while (i > 0 && ${isMin ? 'heap.get(i) < heap.get(parent(i))' : 'heap.get(i) > heap.get(parent(i))'}) {`,
      '        swap(i, parent(i));',
      '        i = parent(i);',
      '    }',
      '}',
    ],
    active: !cur ? -1 : cur.done ? -1 : 4,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <HeapRow array={displayArr} getBoxState={getBoxState} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <MinMaxToggle isMin={isMin} setIsMin={v => { setIsMin(v); reset(); }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={value} onChange={e => setValue(e.target.value)} style={smallInput} placeholder="value" disabled={!!steps} />
            <button onClick={insert} style={smallBtn(V1, !!steps || heap.length >= MAX_SIZE)} disabled={!!steps || heap.length >= MAX_SIZE}>Insert</button>
            <button onClick={reset} style={smallBtn(RED)}>Clear</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          {cur && cur.done && <button onClick={apply} style={smallBtn(GREEN)}>Apply & Insert Next</button>}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            {!cur ? `Insert a value — it starts at the end and "bubbles up" toward the root as long as it beats its parent.` : cur.note}
          </p>
        </div>
      </div>
      <CodePanel title={`Java — Sift-Up (${isMin ? 'Min' : 'Max'}-Heap)`} lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Extract the Root (sift-down) ───────────────────────────────── */
const DEFAULT_MIN_HEAP = [2, 4, 3, 8, 5, 9, 7];
const ExtractMode = () => {
  const [isMin, setIsMin] = useState(true);
  const [heap, setHeap] = useState(DEFAULT_MIN_HEAP);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startExtract = () => {
    if (heap.length === 0) return;
    const arr = [...heap];
    const removed = arr[0];
    const last = arr.pop();
    const built = [{ snapshot: [...arr], removed, note: `Remove the root (${removed}) — record it as the answer.` }];
    if (arr.length === 0) { setSteps(built); setStep(0); return; }
    arr[0] = last;
    built.push({ snapshot: [...arr], i: 0, note: `Move the last element (${last}) to the root, to keep the heap complete.` });
    let i = 0;
    while (true) {
      const l = leftOf(i), r = rightOf(i);
      let target = i;
      if (l < arr.length && better(arr[l], arr[target], isMin)) target = l;
      if (r < arr.length && better(arr[r], arr[target], isMin)) target = r;
      if (target === i) { built.push({ snapshot: [...arr], i, note: `${arr[i]} is in the right place relative to its children — sift-down stops.`, done: true, removed }); break; }
      [arr[i], arr[target]] = [arr[target], arr[i]];
      built.push({ snapshot: [...arr], i: target, swapped: [i, target], note: `${arr[target]} beats index ${i} — swap down to index ${target}.`, removed });
      i = target;
    }
    setSteps(built); setStep(0);
  };
  const apply = () => {
    if (!steps) return;
    setHeap(steps[steps.length - 1].snapshot);
    setSteps(null); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const displayArr = cur ? cur.snapshot : heap;
  const getBoxState = i => {
    if (!cur) return {};
    if (cur.swapped && cur.swapped.includes(i)) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff' };
    if (i === cur.i) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tag: 'sifting down', tagColor: V2 };
    return {};
  };

  const code = {
    lines: [
      'int extractRoot() {',
      '    int root = heap.get(0);',
      '    int last = heap.remove(heap.size() - 1);',
      '    if (!heap.isEmpty()) heap.set(0, last);',
      '    int i = 0;',
      `    while (${isMin ? 'a child is smaller than heap.get(i)' : 'a child is bigger than heap.get(i)'}) {`,
      '        swap(i, thatChild);',
      '        i = thatChild;',
      '    }',
      '    return root;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? -1 : 6,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <HeapRow array={displayArr} getBoxState={getBoxState} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <MinMaxToggle isMin={isMin} setIsMin={v => { setIsMin(v); setSteps(null); }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={startExtract} style={smallBtn(V1, !!steps || heap.length === 0)} disabled={!!steps || heap.length === 0}>Extract Root</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          {cur && <Chip color={GREEN}>removed = {cur.removed}</Chip>}
          {cur && cur.done && <button onClick={apply} style={smallBtn(GREEN)}>Apply</button>}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            {!cur ? `Extracting the root is O(log n): swap in the last element, then "sift down" it into place.` : cur.note}
          </p>
        </div>
      </div>
      <CodePanel title={`Java — Sift-Down (${isMin ? 'Min' : 'Max'}-Heap)`} lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Heapify an Array (bottom-up, O(n)) ─────────────────────────── */
const HeapifyMode = () => {
  const [isMin, setIsMin] = useState(true);
  const [input, setInput] = useState('9, 4, 7, 1, 3, 8, 2');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const arr = input.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
    if (arr.length === 0) return;
    const built = [{ snapshot: [...arr], note: 'Starting array — not a valid heap yet.' }];
    for (let start = Math.floor(arr.length / 2) - 1; start >= 0; start--) {
      built.push({ snapshot: [...arr], i: start, note: `Sift down from index ${start} (the last non-leaf node reached so far).` });
      let i = start;
      while (true) {
        const l = leftOf(i), r = rightOf(i);
        let target = i;
        if (l < arr.length && better(arr[l], arr[target], isMin)) target = l;
        if (r < arr.length && better(arr[r], arr[target], isMin)) target = r;
        if (target === i) break;
        [arr[i], arr[target]] = [arr[target], arr[i]];
        built.push({ snapshot: [...arr], i: target, swapped: [i, target], note: `Swap index ${i} and ${target}.` });
        i = target;
      }
    }
    built.push({ snapshot: [...arr], done: true, note: 'Every non-leaf node processed, from the bottom up — the whole array is now a valid heap.' });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const displayArr = cur ? cur.snapshot : input.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
  const getBoxState = i => {
    if (!cur) return {};
    if (cur.swapped && cur.swapped.includes(i)) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff' };
    if (i === cur.i) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    return {};
  };

  const code = {
    lines: [
      'void heapify(int[] arr) {',
      '    for (int i = arr.length / 2 - 1; i >= 0; i--) {',
      '        siftDown(arr, i, arr.length);',
      '    }',
      '}',
      '// Starting from the LAST non-leaf node and working back to the root',
      '// makes this whole build O(n), not O(n log n).',
    ],
    active: !cur ? -1 : cur.done ? -1 : 2,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <HeapRow array={displayArr} getBoxState={getBoxState} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <MinMaxToggle isMin={isMin} setIsMin={v => { setIsMin(v); setSteps(null); }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} />
            <button onClick={run} style={smallBtn(V1)}>Heapify</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{cur ? cur.note : 'Enter any array, then Heapify — watch it become a valid heap in one bottom-up pass.'}</p>
        </div>
      </div>
      <CodePanel title={`Java — Bottom-Up Heapify (${isMin ? 'Min' : 'Max'})`} lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Kth Largest Tracker (min-heap of size k) ───────────────────── */
const KthMode = () => {
  const [input, setInput] = useState('4, 5, 8, 2, 9, 1, 7');
  const [kInput, setKInput] = useState('3');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const nums = input.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
    const k = Math.max(1, parseInt(kInput, 10) || 1);
    if (nums.length === 0) return;
    let heap = [];
    const built = [];
    const siftUpMin = arr => {
      let i = arr.length - 1;
      while (i > 0 && arr[i] < arr[parentOf(i)]) { [arr[i], arr[parentOf(i)]] = [arr[parentOf(i)], arr[i]]; i = parentOf(i); }
    };
    const siftDownMin = arr => {
      let i = 0;
      while (true) {
        const l = leftOf(i), r = rightOf(i);
        let t = i;
        if (l < arr.length && arr[l] < arr[t]) t = l;
        if (r < arr.length && arr[r] < arr[t]) t = r;
        if (t === i) break;
        [arr[i], arr[t]] = [arr[t], arr[i]];
        i = t;
      }
    };
    nums.forEach(n => {
      heap.push(n);
      siftUpMin(heap);
      if (heap.length > k) {
        heap[0] = heap[heap.length - 1];
        heap.pop();
        siftDownMin(heap);
        built.push({ snapshot: [...heap], num: n, note: `Push ${n}. Heap grew past size ${k} — pop the smallest (the root) to shrink back to size ${k}.` });
      } else {
        built.push({ snapshot: [...heap], num: n, note: `Push ${n}. Heap size is now ${heap.length} (≤ ${k}) — keep everything.` });
      }
    });
    built.push({ snapshot: [...heap], done: true, note: `Every number processed. The heap's root, ${heap[0]}, is the ${k}${k === 1 ? 'st' : k === 2 ? 'nd' : k === 3 ? 'rd' : 'th'} largest value seen so far.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'PriorityQueue<Integer> minHeap = new PriorityQueue<>();   // size k',
      'for (int num : stream) {',
      '    minHeap.add(num);',
      '    if (minHeap.size() > k) {',
      '        minHeap.poll();   // discard the smallest',
      '    }',
      '}',
      'return minHeap.peek();   // the kth largest',
    ],
    active: !cur ? -1 : cur.done ? -1 : 4,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <HeapRow array={cur ? cur.snapshot : []} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Stream</span>
            <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>k</span>
            <input value={kInput} onChange={e => { setKInput(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 60 }} />
            <button onClick={run} style={smallBtn(V1)}>Run</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            {cur ? cur.note : `A min-heap capped at size k — its root is always the kth largest value seen so far. Enter a stream and k, then Run.`}
          </p>
        </div>
      </div>
      <CodePanel title="Java — Kth Largest via a Min-Heap" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const isMin = Math.random() < 0.5;
  const parentVal = 1 + Math.floor(Math.random() * 20);
  const childVal = 1 + Math.floor(Math.random() * 20);
  const shouldSwap = isMin ? childVal < parentVal : childVal > parentVal;
  return { isMin, parentVal, childVal, correct: shouldSwap ? 'swap' : 'noswap' };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const options = [
    { key: 'swap', label: 'Swap them — the child beats the parent' },
    { key: 'noswap', label: "Don't swap — the heap property already holds here" },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', paddingTop: 26, paddingBottom: 20 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', marginBottom: 6 }}>parent</div>
          <div style={{ width: 50, height: 50, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg3)', border: '1.5px solid var(--border)', fontWeight: 800, fontSize: 16 }}>{scenario.parentVal}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', marginBottom: 6 }}>child</div>
          <div style={{ width: 50, height: 50, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg2)', border: `1.5px solid ${V1}`, fontWeight: 800, fontSize: 16 }}>{scenario.childVal}</div>
        </div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
        This is a {scenario.isMin ? 'MIN' : 'MAX'}-heap. The child just moved here during sift-up. What happens next?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const HeapPlayground = () => {
  const [mode, setMode] = useState('build');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'build' && <BuildMode />}
      {mode === 'extract' && <ExtractMode />}
      {mode === 'heapify' && <HeapifyMode />}
      {mode === 'kth' && <KthMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default HeapPlayground;
